import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  Iitem,
  IitemOffer,
  TitemOfferRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { Item } from './item.define';

/**
 * Type alias for item offer properties with specific generic types
 * @template T - Type for amount (default: number)
 * @template U - Type for ID (default: string)
 * @template V - Type for item (default: Iitem)
 */
type TitemOfferProps = IitemOffer<number, string, Iitem>;

/**
 * Represents a special offer or promotion that can be applied to items in the system.
 * This class provides methods to manage item offers, including creation, retrieval,
 * updating, and deletion of offers.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TitemOfferProps - The type definition for item offer properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link ItemOffer} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const itemOffer = await ItemOffer.getAll();
 *
 * const itemOffer = await ItemOffer.filterAll({
 *
 * });
 *
 * const itemOffer = await ItemOffer.bulkDelete({
 *
 * });
 *
 * const itemOffer = await ItemOffer.getOne('resourceId');
 *
 * const itemOffer = await ItemOffer.remove();
 *
 * ```
 */
export class ItemOffer extends DatabaseAuto {
  /** Base API endpoint for item offer operations */
  private static readonly baseUrl = '/item-offer';

  /** Unique reference ID for the offer */
  readonly urId: TitemOfferProps['urId'];

  /** Company ID associated with the offer */
  readonly company: TitemOfferProps['company'];

  /** Array of items included in the offer */
  readonly items: Item[];

  /** Expiration date of the offer */
  readonly expireAt: TitemOfferProps['expireAt'];

  /** Type/category of the offer */
  readonly type: TitemOfferProps['type'];

  /** Main heading/title of the offer */
  readonly header: TitemOfferProps['header'];

  /** Secondary heading/subtitle of the offer */
  readonly subHeader: TitemOfferProps['subHeader'];

  /** Discount or special amount associated with the offer */
  readonly amount: TitemOfferProps['amount'];

  /**
   * Creates a new instance of ItemOffer.
   * @param {TitemOfferProps} data - The item offer data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TitemOfferProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      company: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Company ID must be a non-empty string'
      },
      items: {
        validate: (v) =>
          Array.isArray(v) &&
          v.length > 0 &&
          v.every(item =>
            item !== null &&
            item !== undefined),
        message: 'Items must be a non-empty array with valid items'
      },
      type: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Offer type must be a non-empty string'
      },
      header: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Offer header must be a non-empty string'
      },
      subHeader: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Offer sub-header must be a non-empty string'
      },
      amount: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Amount must be a non-negative number'
      },
      expireAt: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const expireAt = v instanceof Date ? v : new Date(v);

          // Check date validity and ensure it's in the future
          return !isNaN(expireAt.getTime()) && expireAt > new Date();
        },
        message: 'Expiration date must be a valid future date (Date object or ISO date string)'
      }
    });

    super(data);

    this.urId = data.urId;
    this.company = data.company;
    this.items = data.items.map((val) => new Item(val));
    this.type = data.type;
    this.header = data.header;
    this.subHeader = data.subHeader;
    this.amount = data.amount;
    this.expireAt = new Date(data.expireAt);
  }

  /**
   * Retrieves all item offers with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: ItemOffer[] }>}
   * An object containing the total count and an array of item offers
   * @throws {Error} If there's an error fetching the offers
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TitemOfferProps>>(`${ItemOffer.baseUrl}?${query}`);

    const offers = await lastValueFrom(observer$);

    return {
      count: offers.count,
      dataArr: offers.data.map((val) => new ItemOffer(val))
    };
  }

  /**
   * Filters item offers based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: ItemOffer[] }>}
   * An object containing the total count and an array of filtered item offers
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TitemOfferProps>>(
        `${ItemOffer.baseUrl}/filter`,
        filter
      );

    const offers = await lastValueFrom(observer$);

    return {
      count: offers.count,
      dataArr: offers.data.map((val) => new ItemOffer(val))
    };
  }

  /**
   * Retrieves a single item offer by its ID.
   * @param {string} resourceId - The unique identifier of the item offer
   * @returns {Promise<ItemOffer>} The requested item offer
   * @throws {Error} If the offer is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TitemOfferProps>(`${ItemOffer.baseUrl}/${resourceId}`);
    const offer = await lastValueFrom(observer$);

    return new ItemOffer(offer);
  }

  /**
   * Creates a new item offer.
   * @param {TitemOfferRequest<true>} payload - The offer data to create
   * @returns {Promise<ItemOffer>} The newly created item offer
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: TitemOfferRequest<true>): Promise<ItemOffer> {
    const observer$ = CommerceClient.ehttp
      .makePost<TitemOfferProps>(
        `${ItemOffer.baseUrl}`,
        payload
      );

    return new ItemOffer(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple item offers based on the specified criteria.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk deletion operation
   * @throws {Error} If the bulk deletion fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${ItemOffer.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current item offer with new values.
   * @param {Partial<IitemOffer>} payload - The updated offer data
   * @returns {Promise<ItemOffer>} The updated item offer
   * @throws {Error} If the update operation fails or the offer doesn't exist
   */
  async update(payload: Partial<IitemOffer>) {
    const observer$ = CommerceClient.ehttp
      .makePut<TitemOfferProps>(
        `${ItemOffer.baseUrl}/${this._id}`,
        payload
      );

    return new ItemOffer(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current item offer.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} If the deletion fails or the offer doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${ItemOffer.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
